import { ReactNode, useState } from 'react';
import { PermissionScope } from 'types/apps';
import SidebarActionButton from 'ui-component/DetailLayout/SidebarComponents/SidebarActionButton';
import { IconThumbUpOff, IconThumbUp } from '@tabler/icons-react';
import { GenericResource, ToggleMutation } from 'types/api';
import ToggleRecordDialog, {
  ToggleRecordActionTypes,
} from 'ui-component/clientV2/ToggleRecordDialog';
import { TogglePayload } from 'hooks/clientV2/useToggleRecordDialog';
import { NonFormDialogProps } from 'ui-component/extended/ExtendedDialog';

interface SidebarApproveButtonProps<
  T extends GenericResource,
  TrueM extends ToggleMutation<T>,
  FalseM extends ToggleMutation<T>
> {
  disabled?: boolean;
  permissionScope?: PermissionScope;
  approved: boolean;
  idToUpdate: string;
  recordName: string;
  message?: string;
  additionalContent?: string | ReactNode;
  useClientApproveMutation: TrueM;
  useClientUnapproveMutation: FalseM;
  tooltip?: string;
  getApprovePayload?: () =>
    | Promise<TogglePayload<TrueM> | undefined>
    | TogglePayload<TrueM>
    | undefined;
  getUnapprovePayload?: () =>
    | Promise<TogglePayload<FalseM> | undefined>
    | TogglePayload<FalseM>
    | undefined;
  dialogProps?: Omit<
    NonFormDialogProps,
    'onCloseDialog' | 'dialogOpen' | 'title' | 'open'
  >;
}

/**
 * SidebarApproveButtonClientV2
 * To be used specifically with Client V2 api
 * @template T - The resource type extending GenericResource
 * @template TrueM - The approve mutation type
 * @template FalseM - The unapprove mutation type
 */
const SidebarApproveButtonClientV2 = <
  T extends GenericResource,
  TrueM extends ToggleMutation<T>,
  FalseM extends ToggleMutation<T>
>({
  disabled,
  permissionScope,
  approved,
  idToUpdate,
  recordName,
  message,
  additionalContent,
  useClientApproveMutation,
  useClientUnapproveMutation,
  tooltip,
  getApprovePayload,
  getUnapprovePayload,
  dialogProps,
}: SidebarApproveButtonProps<T, TrueM, FalseM>) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <SidebarActionButton
        Icon={approved ? IconThumbUpOff : IconThumbUp}
        label={approved ? 'Unapprove' : 'Approve'}
        tooltip={tooltip || (approved ? 'Unapprove Record' : 'Approve Record')}
        color={approved ? 'error' : 'success'}
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
        permissionScope={permissionScope}
      />
      <ToggleRecordDialog<T, TrueM, FalseM>
        id={idToUpdate}
        isCurrentlyTrue={approved}
        dialogOpen={open}
        onClose={() => {
          setOpen(false);
        }}
        recordName={recordName}
        useToggleTrueMutation={useClientApproveMutation}
        useToggleFalseMutation={useClientUnapproveMutation}
        toggleActionType={ToggleRecordActionTypes.APPROVE}
        message={message}
        additionalContent={additionalContent}
        getToggleOnPayload={getApprovePayload}
        getToggleOffPayload={getUnapprovePayload}
        dialogProps={dialogProps}
      />
    </>
  );
};

export default SidebarApproveButtonClientV2;
